import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  FloorRail,
  LinkButton,
  Modal,
  Sparkline,
  Stat,
} from '@/components/ui';
import { useMarket } from '@/providers/market';
import { compact, eth, share } from '@/lib/format';

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);

/**
 * The three steps of the mechanic, per DESIGN.md §10. The floor share is its own
 * absolute slice of every buy — it is NOT carved out of the protocol fee, and
 * sells never add to the floor.
 */
const FLOOR_STEPS: { label: string; note: string; bar: string }[] = [
  { label: 'Every buy', note: 'a fixed floor share is split off', bar: 'bg-bull' },
  { label: 'Floor reserve', note: 'protocol-owned, never withdrawable', bar: 'bg-floor' },
  { label: 'Floor price', note: 'only ratchets up, redeem any time', bar: 'bg-floor-soft' },
];

/** Tiny div-only flow diagram: buy → floor reserve → redeemable floor price. */
function FloorFlow() {
  return (
    <div className="flex items-stretch gap-1.5" aria-hidden="true">
      {FLOOR_STEPS.map((step, i) => (
        <div key={step.label} className="flex min-w-0 flex-1 items-center gap-1.5">
          <div className="min-w-0 flex-1 rounded-md border border-line bg-elevated px-2 py-2 text-center">
            <div className="truncate text-3xs uppercase tracking-[0.14em] text-subtle">
              {step.label}
            </div>
            <div className={`mt-1.5 h-1.5 w-full rounded-full ${step.bar}`} />
            <div className="mt-1.5 line-clamp-2 text-3xs leading-tight text-muted">{step.note}</div>
          </div>
          {i < FLOOR_STEPS.length - 1 ? (
            <div className="shrink-0 text-2xs text-subtle">→</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function HowTheFloorWorks({ open, onClose }: { open: boolean; onClose: () => void }) {
  const steps = [
    {
      title: 'Every buy is split at the door',
      body: 'A fixed floor share of each buy is routed straight into the floor reserve. It is its own slice of the trade, not a cut of the protocol fee.',
    },
    {
      title: 'The reserve is protocol-owned',
      body: 'Nobody can withdraw it — not the creator, not the team. The floor price is the reserve divided by redeemable supply, and it only ever ratchets up. Sells pay a fee but never add to the floor, and never lower it.',
    },
    {
      title: 'Redeem at floor, any time',
      body: 'Any holder can redeem at floor and burn their tokens for the reserve behind them. It keeps working if the market price collapses, and after trading halts at graduation.',
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="How the floor works"
      description="One mechanic, three steps. It is the whole reason bull.markets exists."
      footer={
        <Button variant="primary" onClick={onClose}>
          Got it
        </Button>
      }
    >
      <div className="space-y-5">
        <FloorFlow />
        <ol className="space-y-3">
          {steps.map((step, i) => (
            <li key={step.title} className="flex gap-3">
              <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border border-line bg-elevated font-mono text-2xs text-bull">
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="font-display text-sm font-semibold text-fg">{step.title}</p>
                <p className="mt-0.5 text-sm leading-relaxed text-muted">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="rounded-lg border border-line bg-elevated/60 px-3 py-2 text-2xs text-muted">
          The floor is a lower bound, never a promise about price. Above the floor, the market
          decides.
        </p>
      </div>
    </Modal>
  );
}

/**
 * The mechanic as one hairline strip. A bordered card here re-spent the vertical
 * budget the hero was supposed to give back, so this stays a single row.
 */
function FloorStrip({ onExplain }: { onExplain: () => void }) {
  return (
    <div className="mt-6 border-t border-line pt-4">
      <ol className="flex flex-wrap items-center gap-x-3.5 gap-y-2 text-2xs">
        {FLOOR_STEPS.map((step, i) => (
          <li key={step.label} className="flex min-w-0 items-center gap-1.5">
            <span className="grid h-4 w-4 shrink-0 place-items-center rounded border border-line bg-elevated font-mono text-3xs text-bull">
              {i + 1}
            </span>
            <span className="whitespace-nowrap font-medium text-fg">{step.label}</span>
            <span className="min-w-0 truncate text-muted">{step.note}</span>
            {i < FLOOR_STEPS.length - 1 ? (
              <span className="shrink-0 text-subtle" aria-hidden="true">
                →
              </span>
            ) : null}
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={onExplain}
            className="whitespace-nowrap text-2xs text-bull underline decoration-dotted underline-offset-2 transition-colors duration-150 ease-swift hover:text-bull-soft"
          >
            Read the detail
          </button>
        </li>
      </ol>
    </div>
  );
}

/**
 * Market header, not a marketing splash: the pitch on the left, the protocol's
 * live numbers and the aggregate floor rail on the right.
 */
export function Hero() {
  const { stats, tokens, floorLockedSeries } = useMarket();
  const [explainerOpen, setExplainerOpen] = useState(false);

  const aggregate = useMemo(() => {
    let floorLocked = 0;
    let liquidity = 0;
    for (const token of tokens) {
      floorLocked += Number.isFinite(token.floorLockedEth) ? token.floorLockedEth : 0;
      liquidity += Number.isFinite(token.liquidityEth) ? token.liquidityEth : 0;
    }
    return {
      floorLocked,
      liquidity,
      ratio: liquidity > 0 ? clamp01(floorLocked / liquidity) : 0,
    };
  }, [tokens]);

  const floorSeries = useMemo(() => floorLockedSeries.map((p) => p.v), [floorLockedSeries]);

  const statItems = [
    {
      label: 'Total volume',
      value: eth(stats.volumeAllTimeEth),
      delta: stats.volume24hChange,
      sub: `${eth(stats.volume24hEth)} in 24h`,
      tone: 'default' as const,
    },
    {
      label: 'Floor locked',
      value: eth(stats.floorLockedEth),
      delta: stats.floorLockedChange,
      sub: 'protocol-owned, permanent',
      tone: 'floor' as const,
    },
    {
      label: 'ETH returned to holders',
      value: eth(stats.ethReturnedEth),
      delta: undefined,
      sub: `${eth(stats.ethBurnedEth)} burned`,
      tone: 'up' as const,
    },
    {
      label: 'Tokens launched',
      value: compact(stats.tokensLaunched, 0),
      delta: undefined,
      sub: `${compact(stats.graduatedCount, 0)} graduated`,
      tone: 'default' as const,
    },
  ];

  return (
    <section className="relative overflow-hidden">
      <div className="texture-grid pointer-events-none absolute inset-0 opacity-70" aria-hidden="true" />
      <div className="bg-grid-fade pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="shell relative py-7 lg:py-9">
        <div className="grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_24rem] lg:gap-10">
          {/* ── pitch ─────────────────────────────────────────────────────── */}
          <div className="min-w-0">
            <p className="text-2xs font-semibold uppercase tracking-[0.14em] text-bull">
              The launchpad with a floor
            </p>

            <h1 className="mt-2.5 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Price discovery with a{' '}
              <span className="text-gradient-bull">floor that only rises</span>.
            </h1>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-muted sm:text-[0.9375rem]">
              Every buy routes a fixed slice into a permanent floor reserve. The floor only ratchets
              up, and any holder can redeem at it, any time.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              <LinkButton to="/launch" variant="primary" size="md">
                Launch a token
              </LinkButton>
              <Button variant="outline" size="md" onClick={() => setExplainerOpen(true)}>
                How the floor works
              </Button>
            </div>

            <FloorStrip onExplain={() => setExplainerOpen(true)} />

            {/* Headline numbers for viewports that do not get the glance panel. */}
            <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-line pt-5 sm:grid-cols-3 lg:hidden">
              {statItems.slice(0, 3).map((item) => (
                <Stat
                  key={item.label}
                  size="sm"
                  label={item.label}
                  value={item.value}
                  delta={item.delta}
                  tone={item.tone}
                />
              ))}
            </div>
          </div>

          {/* ── protocol at a glance ──────────────────────────────────────── */}
          <div className="hidden lg:block">
            <div className="rounded-2xl border border-line bg-surface/90 p-5 shadow-card">
              <div className="flex items-center justify-between gap-3">
                <p className="text-3xs font-semibold uppercase tracking-[0.14em] text-subtle">
                  Protocol at a glance
                </p>
                <Badge variant="floor" size="sm">
                  Floor-first
                </Badge>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-4">
                {statItems.map((item) => (
                  <Stat
                    key={item.label}
                    size="sm"
                    label={item.label}
                    value={item.value}
                    delta={item.delta}
                    sub={item.sub}
                    tone={item.tone}
                  />
                ))}
              </div>

              <div className="rule my-4" />

              <div>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-3xs font-semibold uppercase tracking-[0.14em] text-subtle">
                    Aggregate floor coverage
                  </p>
                  <span className="font-mono text-2xs text-floor">{share(aggregate.ratio)}</span>
                </div>
                <FloorRail
                  className="mt-2"
                  floorRatio={aggregate.ratio}
                  height={10}
                  showLabels
                  floorLabel={eth(aggregate.floorLocked)}
                  spotLabel={`of ${eth(aggregate.liquidity)} liquidity`}
                />
              </div>

              <div className="rule my-4" />

              <div>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-3xs font-semibold uppercase tracking-[0.14em] text-subtle">
                    Floor locked, 90d
                  </p>
                  <span className="font-mono text-2xs text-muted">
                    {eth(stats.floorLockedEth)}
                  </span>
                </div>
                <Sparkline
                  data={floorSeries}
                  tone="floor"
                  area
                  height={52}
                  className="mt-2 h-[52px] w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="shell relative">
        <div className="rule" />
      </div>

      <HowTheFloorWorks open={explainerOpen} onClose={() => setExplainerOpen(false)} />
    </section>
  );
}
